import { call, delay, put, SagaGenerator } from "typed-redux-saga";

import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import {
	ConnectorLocator,
	RestRequestPayload,
	RestServerConnector
} from "@com.mgmtp.a12.utils/utils-connector/lib/main";

export const fetchWrapper: RestServerConnector["fetchData"] = function fetchWrapper(requestPayload) {
	const serverConnector = ConnectorLocator.getInstance().getServerConnector();
	if (!(serverConnector instanceof RestServerConnector)) {
		throw new Error(`"ServerConnector" needs to be a "RestServerConnector".`);
	}

	try {
		return serverConnector.fetchData(requestPayload);
	} catch (e: unknown) {
		return Promise.reject(e);
	}
};

/**
 *
 * @param request
 * @param successCallback _Can_ return a number which indicates a delay in ms before the request is retried (polling!)
 * @param errorCallback
 * @param showProgressIndicator
 */
export function fetchSagaFactory(
	request: RestRequestPayload,
	successCallback: (response: Response) => SagaGenerator<void | number>,
	errorCallback: (exception: unknown) => SagaGenerator<void>,
	showProgressIndicator?: boolean
): () => SagaGenerator<void> {
	return function* fetchSaga() {
		let shouldContinue = true;
		let delayMs = 0;

		while (shouldContinue) {
			if (delayMs > 0) {
				yield* delay(delayMs);
			}
			try {
				if (showProgressIndicator) {
					yield* put(ApplicationActions.setBusy(true));
				}

				// The A12 RestServerConnector always rejects for non-2xx responses,
				// so response here is always a successful 2xx.
				const response = yield* call(fetchWrapper, request);

				const callbackResult = yield* call(successCallback, response);
				shouldContinue = !!callbackResult;
				if (callbackResult) {
					delayMs = callbackResult;
				}
			} catch (error) {
				// The A12 RestServerConnector rejects for non-2xx responses.
				// It may reject with either:
				//   (a) a standard Response object  → extract via response.json()
				//   (b) a custom A12 object {status, content, header, url} where content is already parsed
				// Normalise both shapes into an Error so callers always receive a proper Error.
				if (isA12ResponseLike(error)) {
					const errorMessage = extractMessageFromA12Response(error as A12ResponseLike);
					yield* call(errorCallback, new Error(errorMessage));
				} else if (isResponseLike(error)) {
					const errorMessage = yield* call(extractErrorMessageFromResponse, error as Response);
					yield* call(errorCallback, new Error(errorMessage));
				} else {
					yield* call(errorCallback, error);
				}
				shouldContinue = false;
			} finally {
				if (showProgressIndicator) {
					yield* put(ApplicationActions.setBusy(false));
				}
			}
		}
	};
}

async function extractErrorMessageFromResponse(response: Response): Promise<string> {
	const defaultMessage = `${response.status}: ${response.statusText}`;
	try {
		let parsed: unknown;
		try {
			// Prefer clone to keep original body stream intact
			parsed = await response.clone().json();
		} catch {
			// clone() throws when body was already consumed (A12 RestServerConnector may have read it);
			// fall back to reading the body directly
			parsed = await response.json();
		}
		const body = parsed as Record<string, unknown>;
		if (typeof body?.message === "string" && body.message.length > 0) {
			return body.message;
		}
	} catch {
		// body is not JSON or has no message field — fall back to default
	}
	return defaultMessage;
}

function isResponseLike(error: unknown): boolean {
	return (
		error != null &&
		typeof error === "object" &&
		typeof (error as Record<string, unknown>).json === "function" &&
		typeof (error as Record<string, unknown>).status === "number"
	);
}

/**
 * A12's RestServerConnector rejects non-2xx responses with a custom object
 * { status: number, content: unknown, header: Headers, url: string }
 * where `content` is the already-parsed response body.
 */
interface A12ResponseLike {
	status: number;
	content: unknown;
	header: unknown;
	url: string;
}

function isA12ResponseLike(error: unknown): boolean {
	if (error == null || typeof error !== "object") {
		return false;
	}
	const obj = error as Record<string, unknown>;
	return (
		typeof obj.status === "number" &&
		"content" in obj &&
		"header" in obj &&
		typeof obj.url === "string" &&
		typeof obj.json !== "function" // distinguish from a real Response
	);
}

function extractMessageFromA12Response(response: A12ResponseLike): string {
	const defaultMessage = String(response.status);
	try {
		const content = response.content as Record<string, unknown>;
		if (
			content != null &&
			typeof content === "object" &&
			typeof content.message === "string" &&
			content.message.length > 0
		) {
			return content.message;
		}
	} catch {
		// ignore
	}
	return defaultMessage;
}
